// sortUtils.js

/**
 * Сравнение строк на украинском языке с учетом локали
 */
const compareStrings = (a, b, direction = 'asc') => {
  if (a === undefined || a === null) a = '';
  if (b === undefined || b === null) b = '';
  
  const comparison = String(a).localeCompare(String(b), 'uk');
  return direction === 'asc' ? comparison : -comparison;
};

/**
 * Сравнение дат в формате "DD.MM.YYYY" или "xx.MM.YYYY"
 * (где xx - скрытый день, который считается как 01)
 */
const compareDates = (a, b, direction = 'asc') => {
  const parseDate = (dateStr) => {
    if (!dateStr || dateStr === '') return new Date(0);
    
    const parts = dateStr.split('.');
    if (parts.length !== 3) return new Date(0);
    
    let day = parts[0];
    let month = parts[1];
    let year = parts[2];
    
    // Если день скрыт (например "xx"), заменяем на 01
    if (day.toLowerCase() === '_*_*_' || day === '_*_*_') {
      day = '01';
    }
    
    // Валидация месяца и года
    month = parseInt(month, 10);
    year = parseInt(year, 10);
    day = parseInt(day, 10);
    
    if (isNaN(month) || isNaN(year) || isNaN(day)) {
      return new Date(0);
    }
    
    // Месяцы в JS идут от 0 до 11
    const date = new Date(year, month - 1, day);
    return isNaN(date.getTime()) ? new Date(0) : date;
  };
  
  const dateA = parseDate(a);
  const dateB = parseDate(b);
  
  const comparison = dateA - dateB;
  return direction === 'asc' ? comparison : -comparison;
};

/**
 * Сравнение чисел
 */
const compareNumbers = (a, b, direction = 'asc') => {
  const numA = parseFloat(a);
  const numB = parseFloat(b);
  
  if (isNaN(numA)) return direction === 'asc' ? -1 : 1;
  if (isNaN(numB)) return direction === 'asc' ? 1 : -1;
  
  const comparison = numA - numB;
  return direction === 'asc' ? comparison : -comparison;
};

/**
 * Универсальная функция сравнения в зависимости от типа
 * @param {string|number} a - первое значение
 * @param {string|number} b - второе значение
 * @param {string} type - тип данных: 'text', 'date', 'number'
 * @param {string} direction - направление сортировки: 'asc' или 'desc'
 */
const compareByType = (a, b, type, direction) => {
  switch (type) {
    case 'date':
      return compareDates(a, b, direction);
    case 'number':
      return compareNumbers(a, b, direction);
    case 'text':
    default:
      return compareStrings(a, b, direction);
  }
};

/**
 * Сортировка массива данных по указанному ключу с учетом типа
 * @param {Array} data - массив данных для сортировки
 * @param {string} key - ключ для сортировки
 * @param {string} direction - направление сортировки
 * @param {string} type - тип данных для сравнения
 * @returns {Array} - отсортированный массив
 */
const sortData = (data, key, direction, type = 'text') => {
  if (!key || !data || data.length === 0) return data;
  
  return [...data].sort((a, b) => {
    const aValue = a[key];
    const bValue = b[key];
    return compareByType(aValue, bValue, type, direction);
  });
};

/**
 * Генератор функции сортировки для использования в useMemo
 * @param {Array} data - исходный массив данных
 * @param {Object} sortConfig - конфигурация сортировки { key, direction }
 * @param {Object} columnsConfig - конфигурация колонок с типами
 * @returns {Function} - функция для получения отсортированных данных
 */
const createSortFunction = (data, sortConfig, columnsConfig) => {
  if (!sortConfig.key || !data) return () => data;
  
  const column = columnsConfig.find(col => col.key === sortConfig.key);
  const type = column?.type || 'text';
  
  return () => sortData(data, sortConfig.key, sortConfig.direction, type);
};

export {
  compareStrings,
  compareDates,
  compareNumbers,
  compareByType,
  sortData,
  createSortFunction
};